import React, {
  useCallback, useEffect,
  useMemo,
  useState,
} from 'react';

import { Button } from '@mui/material';
import { env } from 'constants';
import { CartContext } from 'constants/contexts';
import useOverlay from 'hooks/useOverlay';
import usePersistedState from 'hooks/usePersistedState';
import { transformToAnalyticsItems, triggerAnalyticEvent } from 'utils/analytics';
import { captureException } from 'utils/error';
import { getItemTotalPrice } from 'utils/item';
import CartOverlay from './CartOverlay';
import { checkMenuAvailability } from './requests';
import { mergeItems, sortItems } from './utils';

const DEFAULT_STATE = {
  items: [],
  merchant: null,
  minimumOrderPrice: 0,
  scheduledTime: null,
  lastUpdate: null,
  hasDelivery: true,
};

function CartProvider({ children }) {
  const [state, setState] = usePersistedState(DEFAULT_STATE, 'cart-provider');
  const {
    items, merchant, scheduledTime, lastUpdate,
  } = state;
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [openOverlay, closeOverlay] = useOverlay();
  const [loading, setLoading] = useState(false);
  const [openingHours, setOpeningHours] = useState(null);
  const [firstAvailableTime, setFirstAvailableTime] = useState(null);
  const [schedulingError, setSchedulingError] = useState();

  const reset = useCallback((newState = {}) => {
    setState({ ...DEFAULT_STATE, ...newState });
  }, []);

  useEffect(() => {
    if (
      items.length === 0 ||
      !lastUpdate ||
      (lastUpdate > Date.now() - 1000 * 60 * 60 * 5)) {
      return;
    }
    triggerAnalyticEvent('abandoned_cart', {
      value: items.reduce((acc, item) => acc + getItemTotalPrice(item), 0),
      currency: 'USD',
      items: transformToAnalyticsItems({
        items,
      }),
    });
    reset();
  }, [lastUpdate, items, reset]);

  useEffect(() => {
    if (!merchant) {
      return;
    }

    setState((current) => ({ ...current, hasDelivery: Boolean(merchant.has_delivery) }));
    ensureMenuIsAvailable(scheduledTime);
  }, [merchant]);

  useEffect(() => {
    if (!env.IS_CATERING && scheduledTime && scheduledTime < Date.now()) {
      setState((c) => ({ ...c, scheduledTime: null }));
    }
  }, [scheduledTime]);

  const addToCart = useCallback((item) => {
    setState((current) => ({
      ...current,
      items: sortItems(mergeItems({ items: current.items, item })),
      lastUpdate: Date.now(),
    }));
    triggerAnalyticEvent('add_to_cart', {
      value: getItemTotalPrice(item),
      currency: 'USD',
      items: transformToAnalyticsItems({
        items: [item],
        merchant,
      }),
    });
  }, []);

  const changeQuantity = useCallback(
    ({ itemIndex, qty }) => {
      const item = items[itemIndex];
      const newItems = sortItems([
        ...items.filter((_, index) => index !== itemIndex),
        {
          ...item,
          selection: {
            ...item.selection,
            qty,
          },
        },
      ]).filter(({ selection: { qty } }) => qty > 0);
      setState((state) => ({
        ...state,
        lastUpdate: Date.now(),
        items: newItems,
        merchant: newItems.length ? merchant : null,
      }));
      if (qty === 0) {
        triggerAnalyticEvent('remove_from_cart', {
          value: getItemTotalPrice(item),
          currency: 'USD',
          items: transformToAnalyticsItems({
            items: [item],
            merchant,
          }),
        });
      }
    },
    [items],
  );

  const setScheduledTime = useCallback(
    (newScheduledTime) => {
      if (newScheduledTime === scheduledTime) {
        return;
      }
      setState((current) => ({
        ...current,
        // items: [], // TODO: handle unavailable items
        lastUpdate: Date.now(),
        scheduledTime: newScheduledTime,
      }));
    },
    [scheduledTime],
  );

  const getQty = useCallback((itemId) => items.reduce((acc, item) => {
    if (itemId && item.id !== itemId) {
      return acc;
    }
    return acc + (item?.selection?.qty || 0);
  }, 0), [items]);

  const updateMerchant = useCallback(
    ({ onCancel, merchant: newMerchant }) => {
      if (!newMerchant || merchant?.id === newMerchant.id) {
        return;
      }
      if (
        items.length === 0 ||
        !merchant
      ) {
        reset({ merchant: newMerchant });
        return;
      }
      openOverlay({
        title: 'Start a new order?',
        body: `Your cart already contains an item from "${merchant.name}". Would you like to clear the cart and add new items from "${newMerchant.name}"?`,
        hashKey: null,
        actions: (
          <>
            <Button
              onClick={() => {
                onCancel();
                closeOverlay();
              }}
            >
              Go back
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                reset({ merchant: newMerchant });
                closeOverlay();
              }}
            >
              Start a new order
            </Button>
          </>
        ),
        onClose: () => {
          onCancel();
          closeOverlay();
        },
      });
    },
    [merchant, openOverlay, closeOverlay, reset, items.length],
  );

  const handleMenuAvailability = (data, showError) => {
    if (data.first_available_time) {
      setFirstAvailableTime(data.first_available_time);
    }
    setOpeningHours(data.schedule);
    if (showError) {
      setSchedulingError((scheduledTime && data.failed_check) ? 'This caterer is closed at your selected time.' : '');
    }
  };
  const ensureMenuIsAvailable = useCallback((dateTime, showError = true) => {
    if (!merchant) {
      return;
    }
    setLoading(true);
    return checkMenuAvailability({
      dateTime: dateTime ? new Date(dateTime).toISOString() : undefined,
      idOrSlug: merchant.id,
    })
      .then((res) => {
        handleMenuAvailability(res.data.data, showError);
        return res.data.data.failed_check;
      })
      .catch((error) => {
        captureException(error);
      })
      .finally(() => setLoading(false));
  }, [merchant]);

  const contextValue = useMemo(
    () => ({
      setMinimumOrderPrice: (price) => setState((s) => ({ ...s, minimumOrderPrice: price })),
      changeQuantity,
      updateMerchant,
      addToCart,
      openCart: () => setCartModalOpen(true),
      closeCart() {
        setCartModalOpen(false);
      },
      getQty,
      ...state,
      reset,
      setScheduledTime,
      set(payload) {
        setState((current) => ({ ...current, ...payload }));
      },
      isModalOpen: cartModalOpen,
      openingHours,
      menuLoading: loading,
      firstAvailableTime,
      ensureMenuIsAvailable,
      schedulingError,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...Object.values(state),
      getQty,
      addToCart,
      updateMerchant,
      setScheduledTime,
      changeQuantity,
      reset,
      cartModalOpen,
      openingHours,
      loading,
      firstAvailableTime,
      schedulingError,
    ],
  );

  return (
    <CartContext.Provider value={contextValue} displayName="CartProvider">
      {children}
    </CartContext.Provider>
  );
}

CartProvider.CartOverlay = CartOverlay;
export default CartProvider;
